import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Eye, Globe, Info, MapPin, Phone, Plus, Trash2 } from 'lucide-react';
import { FACILITY_LABELS, type ListingOverview, type ListingRow, type OpeningHours } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Button, Checkbox, Field, Input, Select, Textarea, TimePicker } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TYPES = ['hospital', 'lab', 'pharmacy'] as const;

type HourRow = OpeningHours & { rowKey: string };

let hourRowSeq = 0;

function hourRow(entry: OpeningHours): HourRow {
  hourRowSeq += 1;
  return { ...entry, rowKey: `hours-${hourRowSeq}` };
}

const hoursPayload = (rows: HourRow[]): OpeningHours[] => rows.map(({ weekday, opensAt, closesAt }) => ({ weekday, opensAt, closesAt }));

export function ListingPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const returnTo = safeReturnTo(params.get('returnTo'), '/admin/listing');
  const branchId = params.get('branchId') ?? '';

  const listings = useQuery({
    queryKey: ['directory-listings'],
    queryFn: () => get<ListingOverview>('/directory/listings'),
  });
  const row = listings.data?.branches.find((branch) => branch.branchId === branchId) ?? null;

  if (!row) {
    return (
      <FormScreen title="Listing" icon={Globe} backTo={returnTo}>
        <RecordState error={listings.error} missing={!branchId || Boolean(listings.data)} />
      </FormScreen>
    );
  }

  return <ListingForm key={row.branchId} row={row} defaultType={user?.facilityType ?? 'hospital'} returnTo={returnTo} />;
}

function ListingForm({ row, defaultType, returnTo }: { row: ListingRow; defaultType: string; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => ({
    types: row.types.length ? row.types : [defaultType],
    about: row.about ?? '',
    services: row.services.join(', '),
    phone: row.phone ?? '',
    email: row.email ?? '',
    hours: row.openingHours.map(hourRow),
    latitude: row.latitude === null ? '' : String(row.latitude),
    longitude: row.longitude === null ? '' : String(row.longitude),
    isPublished: row.isPublished,
  }));
  const [form, setForm] = useState(initial);
  const coordinate = (value: string) => (value.trim() === '' ? null : Number(value));
  const coordinatesValid =
    (form.latitude.trim() === '') === (form.longitude.trim() === '') &&
    (form.latitude.trim() === '' || (Number.isFinite(coordinate(form.latitude)) && Number.isFinite(coordinate(form.longitude))));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      post('/directory/listings', {
        branchId: row.branchId,
        types: form.types,
        about: form.about.trim() || undefined,
        services: form.services
          .split(',')
          .map((service) => service.trim())
          .filter(Boolean),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        openingHours: hoursPayload(form.hours),
        latitude: coordinate(form.latitude),
        longitude: coordinate(form.longitude),
        isPublished: form.isPublished,
      }),
    onSuccess: () => {
      toast.success(form.isPublished ? 'Listing published — it is live in the directory' : 'Listing saved (not shown in the directory)');
      void queryClient.invalidateQueries({ queryKey: ['directory-listings'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const toggleType = (type: string) =>
    setForm((current) => ({
      ...current,
      types: current.types.includes(type) ? current.types.filter((item) => item !== type) : [...current.types, type],
    }));
  const setHours = (update: (current: HourRow[]) => HourRow[]) => setForm((current) => ({ ...current, hours: update(current.hours) }));

  const ready = form.types.length > 0 && coordinatesValid;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={`Listing for ${row.branchName}`}
      description="Only the details on this screen are ever shown publicly"
      icon={Globe}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      {!row.city && (
        <Alert variant="warning">
          This branch has no city set. Add one under Branches before publishing — patients search
          by city.
        </Alert>
      )}

      <FormSection title={tr('admin', 'identity')} icon={Globe} columns={1}>
        <Field label="List this branch as" required error={form.types.length === 0 ? 'Pick at least one — Save stays off until you do' : undefined}>
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map((type) => (
              <Button
                key={type}
                type="button"
                size="sm"
                variant={form.types.includes(type) ? 'default' : 'outline'}
                onClick={() => toggleType(type)}
              >
                {FACILITY_LABELS[type]}
              </Button>
            ))}
          </div>
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'about')} icon={Info} columns={1}>
        <Field label="About" hint="One or two sentences shown on the listing">
          <Textarea rows={3} value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} />
        </Field>
        <Field label="Services" hint="Comma separated, e.g. Cardiology, MRI, 24/7 Emergency">
          <Input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'contact')} icon={Phone}>
        <Field label="Public phone" hint="Leave blank to use the branch phone">
          <PhoneText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label="Public email">
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'location')} icon={MapPin}>
        <Field
          label="Latitude"
          hint="Lets patients find this branch with “Near me” — copy both numbers from the map pin"
          error={coordinatesValid ? undefined : 'Enter both latitude and longitude as numbers, or leave both blank'}
        >
          <Input inputMode="decimal" placeholder="e.g. 31.5204" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
        </Field>
        <Field label="Longitude">
          <Input inputMode="decimal" placeholder="e.g. 74.3587" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'opening_hours')} icon={Clock} columns={1}>
        <div className="space-y-2">
          {form.hours.length === 0 && <p className="text-sm text-muted-foreground">No hours published.</p>}
          {form.hours.map((entry) => (
            <div key={entry.rowKey} className="grid items-end gap-2 sm:grid-cols-12">
              <div className="sm:col-span-5">
                <Select
                  value={String(entry.weekday)}
                  onChange={(e) =>
                    setHours((current) => current.map((item) => (item.rowKey === entry.rowKey ? { ...item, weekday: Number(e.target.value) } : item)))
                  }
                >
                  {WEEKDAYS.map((day, value) => (
                    <option key={day} value={value}>
                      {day}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="sm:col-span-3">
                <TimePicker
                  value={entry.opensAt}
                  onChange={(value) => setHours((current) => current.map((item) => (item.rowKey === entry.rowKey ? { ...item, opensAt: value } : item)))}
                />
              </div>
              <div className="sm:col-span-3">
                <TimePicker
                  value={entry.closesAt}
                  onChange={(value) => setHours((current) => current.map((item) => (item.rowKey === entry.rowKey ? { ...item, closesAt: value } : item)))}
                />
              </div>
              <div className="sm:col-span-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove day"
                  onClick={() => setHours((current) => current.filter((item) => item.rowKey !== entry.rowKey))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setHours((current) => [...current, hourRow({ weekday: 1, opensAt: '09:00', closesAt: '21:00' })])}
          >
            <Plus className="me-1 h-3.5 w-3.5" /> Add a day
          </Button>
        </div>
      </FormSection>

      <FormSection title={tr('admin', 'status')} icon={Eye} columns={1}>
        <Checkbox
          checked={form.isPublished}
          onCheckedChange={(checked) => setForm({ ...form, isPublished: checked })}
          label="Show this branch in the public directory"
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Save" loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
